import { isEqual } from "es-toolkit";
import { isEmpty } from "es-toolkit/compat";
import { atom } from "jotai";
import { atomFamily, atomWithRefresh, loadable } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $accessTokenFunc, $isAuthenticated } from "../authenication";
import {
	$document,
	$documentLocalPath,
	docsListCollabEntry,
	docsListItemRemoteEntry,
} from "../document-list";
import { $internet } from "../utils";
import { $git } from "./git.atom";
import type {
	BranchInfo,
	GitAheadBehind,
	GitBranchAheadBehindInput,
	GitBranchAnalyzeInput,
	GitBranchDeleteInput,
	GitBranchNewInput,
	GitBranchSwitchInput,
	GitBranchTrackInput,
	GitCommitInput,
	GitDescribeInput,
	GitDiff,
	GitDiffRefsInput,
	GitFetchInput,
	GitMergeAnalysis,
	GitMoveCommitInput,
	GitPushInput,
	GitRemoteAddInput,
	GitRepoHistory,
	GitRepoStatus,
	GitRestoreToParentInput,
	GitShow,
	GitShowInput,
	GitStashPopInput,
	GitStashSaveInput,
} from "./git.types";

const $purgeDocument = import("../document-loader/loader.atom").then(
	(module) => module.$purgeDocument
);

const logger = new Logger("git-repository");

// Constants from the old machine
const GIT_PUBLISHING_BRANCH_NAME = "main";
const GIT_PUBLISHING_REMOTE_NAME = "moment";

// ============================================================================
// State Atoms - These represent the current state of the git repository
// ============================================================================

/**
 * Git Status - Contains the current status of the repository.
 *
 * Supports two write modes:
 * - `set(atom)` — triggers a fresh fetch from the Tauri backend
 * - `set(atom, value)` — directly injects a value (used by commitAndRefresh
 *   to avoid redundant jj_snapshot calls)
 */
export const $gitStatus = atomFamily((documentId: string | undefined) => {
	const refreshCounter = atom(0);
	refreshCounter.debugPrivate = true;
	const override = atom(undefined as GitRepoStatus | undefined);
	override.debugPrivate = true;

	const statusAtom = atom(
		async (get): Promise<GitRepoStatus | undefined> => {
			get(refreshCounter);

			const injected = get(override);
			if (injected !== undefined) {
				return injected;
			}

			if (!documentId) {
				return undefined;
			}

			const path = await get($documentLocalPath(documentId));
			if (!path) {
				return undefined;
			}

			const git = get($git);
			return git.status({ path });
		},
		(_get, set, newValue?: GitRepoStatus | undefined) => {
			if (newValue !== undefined) {
				set(override, newValue);
			} else {
				set(override, undefined);
				set(refreshCounter, (c) => c + 1);
			}
		}
	);
	statusAtom.debugLabel = `gitStatus/${documentId}`;
	return statusAtom;
}, isEqual);

/**
 * Whether the document's current branch has unresolved conflicts —
 * either on the working-copy commit itself or on any ancestor reachable
 * by walking first parents from the WC.
 *
 * A user's resolve flow can leave a tree-level conflict on an ancestor
 * (e.g. the rebased "local" commit) while the empty draft on top stays
 * clean. Without checking ancestors, the editor would render in normal
 * mode and let the user write over the document while the conflict
 * markers are still serialized into the file content — corrupting it
 * further.
 *
 * We scope the check to first-parent ancestors of the WC (rather than
 * "any visible commit in the timeline") so conflicts on unrelated
 * branches or detached commits don't lock the editor for the current
 * branch.
 *
 * `$gitStatus` (the WC-local check) is consulted as a fallback when the
 * history hasn't loaded yet or the current commit can't be located in
 * the history (race after a rewrite).
 */
export const $documentHasConflicts = atomFamily((documentId: string | undefined) => {
	const conflictAtom = atom(async (get) => {
		const status = await get($gitStatus(documentId));
		const history = await get($gitHistory(documentId));

		if (status?.commit && history?.commits.length) {
			// Build a hash → commit lookup so we can walk first-parents
			// in O(depth) instead of scanning the array each step.
			const byHash = new Map(history.commits.map((c) => [c.hash, c]));
			const visited = new Set<string>();
			let cursor: string | undefined = status.commit;
			while (cursor && !visited.has(cursor)) {
				visited.add(cursor);
				const commit = byHash.get(cursor);
				if (!commit) {
					// `status.commit` (or one of its ancestors) isn't in
					// the history payload. This happens transiently right
					// after a rewrite (the new WC hash exists in jj's
					// store but our cached `$gitHistory` was fetched
					// before it landed) and on truncated/paginated
					// history responses. Returning `false` here would
					// silently treat a conflicted document as clean and
					// unblock editing/publishing — fall back to the
					// WC-local `status.isConflicted` check instead, which
					// jj answers from the live workspace state and so
					// can't be stale relative to the rewrite.
					return status.isConflicted ?? false;
				}
				if (commit.has_conflict) {
					return true;
				}
				cursor = commit.parents[0];
			}
			return false;
		}

		// Fallback: history not loaded yet or no WC commit at all.
		return status?.isConflicted ?? false;
	});
	conflictAtom.debugLabel = `documentHasConflicts/${documentId}`;
	return conflictAtom;
}, isEqual);

/**
 * Whether the conflict is collab-originated (on collab/main with active collab).
 * Only resolves to true when the document is conflicted AND has collab on collab/main.
 */
export const $documentIsCollabConflict = atomFamily((documentId: string | undefined) => {
	const collabConflictAtom = atom(async (get) => {
		const hasConflicts = await get($documentHasConflicts(documentId));
		if (!hasConflicts || !documentId) {
			return false;
		}

		const status = await get($gitStatus(documentId));
		if (status?.branch !== "collab/main") {
			return false;
		}

		const document = get($document(documentId));
		if (!document) {
			return false;
		}

		const remoteEntry = docsListItemRemoteEntry(document);
		const collabEntry = docsListCollabEntry(remoteEntry);
		return !!collabEntry && !isEmpty(collabEntry);
	});
	collabConflictAtom.debugLabel = `documentIsCollabConflict/${documentId}`;
	return collabConflictAtom;
}, isEqual);

/**
 * Git Branches - List of all local branches
 */
export const $gitBranches = atomFamily((documentId: string | undefined) => {
	const refreshCounter = atom(0);
	refreshCounter.debugPrivate = true;
	const override = atom(undefined as BranchInfo[] | undefined);
	override.debugPrivate = true;

	const branchesAtom = atom(
		async (get): Promise<BranchInfo[] | undefined> => {
			get(refreshCounter);

			const injected = get(override);
			if (injected !== undefined) {
				return injected;
			}

			if (!documentId) {
				return undefined;
			}

			const path = await get($documentLocalPath(documentId));
			if (!path) {
				return [];
			}

			const git = get($git);
			return git.branchList({ path });
		},
		(_get, set, newValue?: BranchInfo[]) => {
			if (newValue !== undefined) {
				set(override, newValue);
			} else {
				set(override, undefined);
				set(refreshCounter, (c) => c + 1);
			}
		}
	);
	branchesAtom.debugLabel = `gitBranches/${documentId}`;
	return branchesAtom;
}, isEqual);

/**
 * Git Remotes - List of all remotes
 */
export const $gitRemotes = atomFamily((documentId: string | undefined) => {
	const remotesAtom = atomWithRefresh(async (get) => {
		if (!documentId) {
			return undefined;
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			return [];
		}

		const git = get($git);
		return git.remoteList({ path });
	});
	remotesAtom.debugLabel = `gitRemotes/${documentId}`;
	return remotesAtom;
}, isEqual);

/**
 * Git History - Commit history
 */
export const $gitHistory = atomFamily((documentId: string | undefined) => {
	const refreshCounter = atom(0);
	refreshCounter.debugPrivate = true;
	const override = atom(undefined as GitRepoHistory | undefined);
	override.debugPrivate = true;

	const historyAtom = atom(
		async (get): Promise<GitRepoHistory | undefined> => {
			get(refreshCounter);

			const injected = get(override);
			if (injected !== undefined) {
				return injected;
			}

			if (!documentId) {
				return undefined;
			}

			const path = await get($documentLocalPath(documentId));
			if (!path) {
				return undefined;
			}

			const git = get($git);
			return git.repoHistory({ path });
		},
		(_get, set, newValue?: GitRepoHistory) => {
			if (newValue !== undefined) {
				set(override, newValue);
			} else {
				set(override, undefined);
				set(refreshCounter, (c) => c + 1);
			}
		}
	);
	historyAtom.debugLabel = `gitHistory/${documentId}`;
	return historyAtom;
}, isEqual);

/**
 * Publish Branch Commit - The commit hash where moment/main points
 * Only available when the document is published and has the remote set up
 */
export const $publishBranchCommit = atomFamily((documentId: string | undefined) => {
	const publishBranchAtom = atomWithRefresh(async (get) => {
		if (!documentId) {
			return undefined;
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			return undefined;
		}

		const git = get($git);
		const refspec = `refs/remotes/${GIT_PUBLISHING_REMOTE_NAME}/${GIT_PUBLISHING_BRANCH_NAME}`;
		return git.resolveRef({ path, refspec });
	});
	publishBranchAtom.debugLabel = `publishBranchCommit/${documentId}`;
	return publishBranchAtom;
}, isEqual);

// ============================================================================
// Mutation Atoms - These represent git operations that can be performed
// ============================================================================

/**
 * Git Commit - Commit current changes
 */
export const $gitCommit = atomFamily((documentId: string | undefined) => {
	const commitAtom = atom(null, async (get, set, input: GitCommitInput) => {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);

		// Use commitAndRefresh to commit + snapshot + read state in a single
		// lock acquisition. This avoids redundant jj_snapshot calls that would
		// otherwise occur when status, history, and branches are fetched as
		// separate commands.
		const result = await git.commitAndRefresh({ path, message: input.message });

		// Inject combined result directly into atoms to avoid redundant
		// jj_snapshot calls from separate status/history/branches fetches.
		if (result.status) {
			set($gitStatus(documentId), result.status);
		}
		set($gitHistory(documentId), result.history);
		set($gitBranches(documentId), result.branches);

		// The committed WC produces a new commit hash and shifts any
		// cached `$gitShow` entries' notion of "HEAD" — drop the cache
		// so subsequent diffs fetch fresh.
		purgeGitShowCache(documentId);

		// Refresh atoms that weren't included in the combined result
		set($publishBranchCommit(documentId));
		set(
			$gitBranchAheadBehind({
				documentId,
				source: "main",
				target: `refs/remotes/${GIT_PUBLISHING_REMOTE_NAME}/${GIT_PUBLISHING_BRANCH_NAME}`,
			})
		);

		return result.describedCommitHash;
	});
	commitAtom.debugLabel = `gitCommit/${documentId}`;
	return commitAtom;
}, isEqual);

/**
 * Git Describe - Update the message of the current working copy commit (jj describe)
 */
export const $gitDescribe = atomFamily((documentId: string | undefined) => {
	const describeAtom = atom(null, async (get, set, input: GitDescribeInput) => {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		const result = await git.describe({
			path,
			message: input.message,
			revision: input.revision,
		});

		set($gitStatus(documentId));
		set($gitHistory(documentId));
		set($gitBranches(documentId));
		// jj_describe rewrites the commit's message, producing a new
		// hash; drop stale $gitShow cache entries.
		purgeGitShowCache(documentId);
		set(
			$gitBranchAheadBehind({
				documentId,
				source: "main",
				target: `refs/remotes/${GIT_PUBLISHING_REMOTE_NAME}/${GIT_PUBLISHING_BRANCH_NAME}`,
			})
		);

		return result;
	});
	describeAtom.debugLabel = `gitDescribe/${documentId}`;
	return describeAtom;
}, isEqual);

export const $gitConfigDraft = atom(
	undefined as Record<string, string | number | null> | undefined
);
$gitConfigDraft.debugLabel = `gitConfigDraft`;

export const $gitConfig = atom(
	async (get): Promise<Record<string, string | number | null>> => {
		const draft = get($gitConfigDraft);
		if (draft) {
			return draft;
		}

		const git = get($git);
		const config = await git.configGet();
		return config;
	},
	async (get, set, input: { key: string; value: string | number | null } | undefined) => {
		const git = get($git);

		// refresh the config
		if (!input) {
			const config = await get($git).configGet();
			set($gitConfigDraft, config);
			return;
		}

		const { key, value } = input;

		const config = await git.configGet();
		await git.configSet({ newConfig: { ...config, [key]: value } });
		// Refresh the draft atom
		set($gitConfigDraft, { ...config, [key]: value });
	}
);
$gitConfig.debugLabel = `gitConfig`;

/**
 * Git Diff - Get diff for working directory
 */
export const $gitDiff = atomFamily((documentId: string | undefined) => {
	const diffAtom = atomWithRefresh(async (get) => {
		if (!documentId) {
			return undefined;
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		return git.diff({ path });
	});
	diffAtom.debugLabel = `gitDiff/${documentId}`;
	return diffAtom;
}, isEqual);

/**
 * Git Diff Refs - Get diff between two refs
 */
export const $gitDiffRefs = atomFamily(
	(input: { documentId: string | undefined } & GitDiffRefsInput) => {
		const { documentId, aSide, bSide } = input;
		const diffRefsAtom = atom(async (get): Promise<GitDiff | undefined> => {
			if (!documentId) {
				return undefined;
			}

			const path = await get($documentLocalPath(documentId));
			if (!path) {
				throw new Error("Document path not found");
			}

			const git = get($git);
			return git.diffRefs({ path, aSide, bSide });
		});
		diffRefsAtom.debugLabel = `gitDiffRefs/${documentId}/${aSide}/${bSide}`;
		return diffRefsAtom;
	},
	isEqual
);

/**
 * Git Fetch - Fetch from remote
 */
export const $gitFetch = atom(
	null,
	async (get, set, params: { input: GitFetchInput; documentId: string }) => {
		const { input, documentId } = params;

		const isAuthenticated = get($isAuthenticated);
		if (!isAuthenticated) {
			logger.error("Cannot fetch: user is not authenticated", { documentId });
			throw new Error("Cannot fetch: user is not authenticated");
		}

		const isOnline = get($internet);
		if (!isOnline) {
			logger.error("Cannot fetch: user is offline", { documentId });
			throw new Error("Cannot fetch: user is offline");
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const statusLoadable = get(loadable($gitStatus(documentId)));
		const status = statusLoadable.state === "hasData" ? statusLoadable.data : undefined;
		const git = get($git);

		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			throw new Error("No access token function found");
		}

		const token = await getAccessTokenSilently();

		const remote = input.remote ?? GIT_PUBLISHING_REMOTE_NAME;
		const branch = input.branch ?? status?.branch ?? GIT_PUBLISHING_BRANCH_NAME;

		await git.fetch({ path, remote, branch, token });

		// Refresh status and ahead/behind after fetch
		set($gitStatus(documentId));
		set(
			$gitBranchAheadBehind({
				documentId,
				source: GIT_PUBLISHING_BRANCH_NAME,
				target: `refs/remotes/${GIT_PUBLISHING_REMOTE_NAME}/${GIT_PUBLISHING_BRANCH_NAME}`,
			})
		);
		set(
			$gitBranchAheadBehind({
				documentId,
				source: "collab/main",
				target: `refs/remotes/${GIT_PUBLISHING_REMOTE_NAME}/collab/main`,
			})
		);
	}
);
$gitFetch.debugLabel = `gitFetch`;

/**
 * Git Pull - Pull from remote
 */
export const $gitPull = atomFamily((documentId: string | undefined) => {
	const pullAtom = atom(null, async (get, set) => {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);

		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			throw new Error("No access token function found");
		}

		const token = await getAccessTokenSilently();
		await git.pull({ path, token });

		// Refresh git state after pull
		set($gitStatus(documentId));
		set($gitHistory(documentId));

		// Purge and reload the document since files on disk may have changed
		set(await $purgeDocument, documentId);
	});
	pullAtom.debugLabel = `gitPull/${documentId}`;
	return pullAtom;
}, isEqual);

/**
 * Git Push - Push to remote
 */
export const $gitPush = atom(
	null,
	async (get, set, params: { input: GitPushInput; documentId: string }) => {
		const { input, documentId } = params;

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const statusLoadable = get(loadable($gitStatus(documentId)));
		const status = statusLoadable.state === "hasData" ? statusLoadable.data : undefined;
		const remotesLoadable = get(loadable($gitRemotes(documentId)));
		const remotes = remotesLoadable.state === "hasData" ? remotesLoadable.data : [];
		const git = get($git);

		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			throw new Error("No access token function found");
		}

		const token = await getAccessTokenSilently();
		const remote = input.remote ?? remotes?.[0]?.name ?? GIT_PUBLISHING_REMOTE_NAME;
		const branch = input.branch ?? status?.branch ?? GIT_PUBLISHING_BRANCH_NAME;

		await git.push({ path, remote, branch, token, sourceCommit: input.sourceCommit });

		// Refresh status and publish branch commit after push
		set($gitStatus(documentId));
		set($publishBranchCommit(documentId));
	}
);
$gitPush.debugLabel = `gitPush`;

/**
 * Git Remote Add - Add a new remote
 */
export const $gitRemoteAdd = atomFamily((documentId: string | undefined) => {
	const remoteAddAtom = atom(null, async (get, set, input: GitRemoteAddInput) => {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		await git.remoteAdd({ path, remote: input.remote, url: input.url });

		// Refresh remotes after adding
		set($gitRemotes(documentId));
	});
	remoteAddAtom.debugLabel = `gitRemoteAdd/${documentId}`;
	return remoteAddAtom;
}, isEqual);

/**
 * Git Branch New - Create a new branch
 */
export const $gitBranchNew = atomFamily((documentId: string | undefined) => {
	const branchNewAtom = atom(null, async (get, set, input: GitBranchNewInput) => {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		await git.branchNew({ path, name: input.name, startPoint: input.startPoint });

		// Refresh git state after creating (branch_new also checks out the new branch)
		set($gitBranches(documentId));
		set($gitStatus(documentId));
		set($gitHistory(documentId));

		// Purge and reload the document since working copy has changed
		set(await $purgeDocument, documentId);
	});
	branchNewAtom.debugLabel = `gitBranchNew/${documentId}`;
	return branchNewAtom;
}, isEqual);

/**
 * Git Branch Switch - Switch to a different branch
 */
export const $gitBranchSwitch = atom(
	null,
	async (
		get,
		set,
		input: {
			documentId: string | undefined;
			input: GitBranchSwitchInput;
		}
	): Promise<void> => {
		if (!input.documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(input.documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		await git.branchSwitch({ path, target: input.input.target });

		// Refresh git state after switching
		set($gitBranches(input.documentId));
		set($gitStatus(input.documentId));
		set($gitHistory(input.documentId));

		// Purge and reload the document since files on disk have changed
		set(await $purgeDocument, input.documentId);
	}
);
$gitBranchSwitch.debugLabel = `gitBranchSwitch`;

/**
 * Git Move Commit - Move a head commit from its current branch to a target branch
 */
export const $gitMoveCommit = atom(
	null,
	async (
		get,
		set,
		input: {
			documentId: string | undefined;
			input: GitMoveCommitInput;
		}
	): Promise<{ newCommitHash: string }> => {
		if (!input.documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(input.documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		const result = await git.moveCommit({
			path,
			commitHash: input.input.commitHash,
			targetBranch: input.input.targetBranch,
		});

		// Refresh git state after moving (branches, status, and history all change)
		set($gitBranches(input.documentId));
		set($gitStatus(input.documentId));
		set($gitHistory(input.documentId));

		// Purge and reload the document since files on disk have changed
		set(await $purgeDocument, input.documentId);

		return result;
	}
);
$gitMoveCommit.debugLabel = `gitMoveCommit`;

/**
 * Git Rebase Bookmark - Rebase local bookmark history onto a target ref.
 *
 * This rebases the local stack (including `@`) onto `targetRef`, exports git
 * refs, and purges the document so it reloads with the new content.
 */
export const $gitRebaseBookmark = atom(
	null,
	async (
		get,
		set,
		params: {
			documentId: string;
			localBranch: string;
			targetRef: string;
		}
	) => {
		const { documentId, localBranch, targetRef } = params;

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		await git.rebaseBookmark({ path, localBranch, targetRef });

		// Refresh git state
		set($gitBranches(documentId));
		set($gitStatus(documentId));
		set($gitHistory(documentId));
		set(
			$gitBranchAheadBehind({
				documentId,
				source: GIT_PUBLISHING_BRANCH_NAME,
				target: `refs/remotes/${GIT_PUBLISHING_REMOTE_NAME}/${GIT_PUBLISHING_BRANCH_NAME}`,
			})
		);
		set(
			$gitBranchAheadBehind({
				documentId,
				source: "collab/main",
				target: `refs/remotes/${GIT_PUBLISHING_REMOTE_NAME}/collab/main`,
			})
		);
		set($publishBranchCommit(documentId));

		// Purge and reload the document since files on disk have changed
		set(await $purgeDocument, documentId);
	}
);
$gitRebaseBookmark.debugLabel = "gitRebaseBookmark";

/**
 * Git Branch Delete - Delete a branch
 */
export const $gitBranchDelete = atomFamily((documentId: string | undefined) => {
	const branchDeleteAtom = atom(null, async (get, set, input: GitBranchDeleteInput) => {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		await git.branchDelete({ path, name: input.name, force: input.force });

		// Refresh branches after deleting
		set($gitBranches(documentId));
	});
	branchDeleteAtom.debugLabel = `gitBranchDelete/${documentId}`;
	return branchDeleteAtom;
}, isEqual);

/**
 * Git Branch Track - Set upstream branch
 */
export const $gitBranchTrack = atom(
	null,
	async (
		get,
		set,
		params: {
			input: GitBranchTrackInput;
			documentId: string | undefined;
		}
	) => {
		if (!params.documentId) {
			throw new Error("Document ID is required");
		}
		const { input, documentId } = params;

		const path = await get($documentLocalPath(params.documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);

		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			throw new Error("No access token function found");
		}

		const token = await getAccessTokenSilently();

		await git.branchTrack({
			path,
			remote: input.remote,
			localBranch: input.localBranch,
			upstreamBranch: input.upstreamBranch,
			token,
		});

		// Refresh branches after tracking
		set($gitBranches(documentId));
	}
);
$gitBranchTrack.debugLabel = `gitBranchTrack`;

/**
 * Git Branch Analyze - Analyze merge possibility
 */
export const $gitBranchAnalyze = atomFamily((documentId: string | undefined) => {
	const branchAnalyzeAtom = atom(
		null,
		async (get, _set, input: GitBranchAnalyzeInput): Promise<GitMergeAnalysis> => {
			if (!documentId) {
				throw new Error("Document ID is required");
			}

			const path = await get($documentLocalPath(documentId));
			if (!path) {
				throw new Error("Document path not found");
			}

			const git = get($git);
			return git.branchAnalyze({ path, source: input.source, target: input.target });
		}
	);
	branchAnalyzeAtom.debugLabel = `gitBranchAnalyze/${documentId}`;
	return branchAnalyzeAtom;
}, isEqual);

/**
 * Git Branch Ahead Behind - Check how many commits ahead/behind
 */
export const $gitBranchAheadBehind = atomFamily((input: GitBranchAheadBehindInput) => {
	const branchAheadBehindAtom = atomWithRefresh(async (get): Promise<GitAheadBehind> => {
		const path = await get($documentLocalPath(input.documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		return git.branchAheadBehind({ path, source: input.source, target: input.target });
	});
	branchAheadBehindAtom.debugLabel = `gitBranchAheadBehind/${input.documentId}/${input.source}/${input.target}`;
	return branchAheadBehindAtom;
}, isEqual);

/**
 * Git Stash Save - Stash current changes
 */
export const $gitStashSave = atomFamily((documentId: string | undefined) => {
	const stashSaveAtom = atom(null, async (get, set, input: GitStashSaveInput = {}) => {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		await git.stashSave({ path, message: input.message });

		// Refresh status after stashing
		set($gitStatus(documentId));
	});
	stashSaveAtom.debugLabel = `gitStashSave/${documentId}`;
	return stashSaveAtom;
}, isEqual);

/**
 * Git Stash Pop - Apply and remove stashed changes
 */
export const $gitStashPop = atomFamily((documentId: string | undefined) => {
	const stashPopAtom = atom(null, async (get, set, input: GitStashPopInput) => {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		await git.stashPop({ path, index: input.index });

		// Refresh git state after popping stash
		set($gitStatus(documentId));

		// Purge and reload the document since files on disk may have changed
		set(await $purgeDocument, documentId);
	});
	stashPopAtom.debugLabel = `gitStashPop/${documentId}`;
	return stashPopAtom;
}, isEqual);

/**
 * Git Show - Show details of a specific commit
 */
export const $gitShow = atomFamily(
	({ documentId, commitHash }: { documentId: string | undefined } & GitShowInput) => {
		const showAtom = atomWithRefresh(async (get): Promise<GitShow | undefined> => {
			if (!documentId) {
				return undefined;
			}

			const path = await get($documentLocalPath(documentId));
			if (!path) {
				throw new Error("Document path not found");
			}

			const git = get($git);
			return git.show({ path, commitHash });
		});
		showAtom.debugLabel = `gitShow/${documentId}/${commitHash}`;
		return showAtom;
	},
	isEqual
);

/**
 * Drop every cached `$gitShow` entry tied to `documentId`.
 *
 * `$gitShow` is an `atomFamily` keyed by `{documentId, commitHash}` with
 * no automatic invalidation. Repo-rewriting ops (resolve / undo / restore
 * / commit / describe) produce new commit hashes; the OLD hashes' atom
 * instances stay cached until this function is called. Without the purge
 * the dialog can render a commit's title while the diff below it comes
 * from a stale cached fetch of a different hash — the exact symptom
 * seen after repeated resolve/undo cycles on the same commit.
 *
 * Call this from any mutation atom that rewrites the op log or commit
 * graph so the next `$gitShow` read goes back to the Tauri backend for
 * fresh data.
 */
const purgeGitShowCache = (documentId: string | undefined): void => {
	if (!documentId) {
		return;
	}
	const keysToRemove: { documentId: string | undefined; commitHash: string }[] = [];
	for (const key of $gitShow.getParams()) {
		if (key.documentId === documentId) {
			keysToRemove.push(key);
		}
	}
	for (const key of keysToRemove) {
		$gitShow.remove(key);
	}
};

/**
 * Git Diff Or Show - Unified atom for getting either diff or show data
 * This atom delegates to either $gitDiff or $gitShow based on the type parameter
 */
export const $gitDiffOrShow = atomFamily(
	({
		documentId,
		type,
		refspec,
	}: {
		documentId: string | undefined;
		type: "show" | "diff";
		refspec?: string;
	}) => {
		const diffOrShowAtom = atom(
			async (get) => {
				if (type === "show") {
					const showAtom = $gitShow({ documentId, commitHash: refspec ?? "HEAD" });
					return get(showAtom);
				} else {
					const diffAtom = $gitDiff(documentId);
					return get(diffAtom);
				}
			},
			async (_get, set) => {
				// When refresh is called, trigger refresh on the underlying atom
				if (type === "show") {
					const showAtom = $gitShow({ documentId, commitHash: refspec ?? "HEAD" });
					set(showAtom);
				} else {
					const diffAtom = $gitDiff(documentId);
					set(diffAtom);
				}
			}
		);
		diffOrShowAtom.debugLabel = `gitDiffOrShow/${documentId}/${type}/${refspec ?? "working"}`;
		return diffOrShowAtom;
	},
	isEqual
);

/**
 * Input type for resolving a conflict
 */
export type GitResolveConflictInput = {
	commitHash: string;
	filePath: string;
	resolvedContent: string;
};

/**
 * Git Resolve Conflict - Resolve a conflict in a JJ commit
 *
 * This rewrites the commit with resolved file content, rebases descendants,
 * and returns the new commit hash.
 */
export const $gitResolveConflict = atomFamily((documentId: string | undefined) => {
	const resolveAtom = atom(
		null,
		async (get, set, input: GitResolveConflictInput): Promise<{ newCommitHash: string }> => {
			if (!documentId) {
				throw new Error("Document ID is required");
			}

			const path = await get($documentLocalPath(documentId));
			if (!path) {
				throw new Error("Document path not found");
			}

			const git = get($git);
			const result = await git.resolveConflict({
				path,
				commitHash: input.commitHash,
				filePath: input.filePath,
				resolvedContent: input.resolvedContent,
			});

			// Refresh history and status after resolving conflict (commits may have been
			// rewritten and the working copy conflict state may have changed).
			// Drop cached `$gitShow` entries because the rewritten commit +
			// its descendants now have new hashes; the old hashes' cached
			// diffs are stale relative to the new parent-tree references.
			set($gitHistory(documentId));
			set($gitStatus(documentId));
			purgeGitShowCache(documentId);

			return result;
		}
	);
	resolveAtom.debugLabel = `gitResolveConflict/${documentId}`;
	return resolveAtom;
}, isEqual);

/**
 * Git Restore to Parent - Restore a conflicted commit's tree to its parent's tree.
 *
 * This abandons local changes and accepts the parent's state (e.g., the server's
 * version during collab conflict resolution). After restoration, the conflict is
 * resolved and the document loader can reconnect to collab.
 */
export const $gitRestoreToParent = atomFamily((documentId: string | undefined) => {
	const restoreAtom = atom(
		null,
		async (get, set, input: GitRestoreToParentInput): Promise<{ newCommitHash: string }> => {
			if (!documentId) {
				throw new Error("Document ID is required");
			}

			const path = await get($documentLocalPath(documentId));
			if (!path) {
				throw new Error("Document path not found");
			}

			const git = get($git);
			const result = await git.restoreToParent({
				path,
				commitHash: input.commitHash,
			});

			// Refresh history and status after restoring (commits have been rewritten
			// and the working copy conflict state should now be resolved).
			// Drop cached `$gitShow` entries — the restored commit has a new
			// hash and any descendants were rebased.
			set($gitHistory(documentId));
			set($gitStatus(documentId));
			purgeGitShowCache(documentId);

			// Deliberately do NOT call `$purgeDocument` here — same
			// reason as `$gitUndo` / `$gitResolveConflict`: purging
			// mid-session unmounts the versioning dialog tree and
			// destroys local session state (undoableCount, blade
			// selection, autoOpen guards). Callers that need a
			// document reload after restore (e.g. the collab "accept
			// server version" flow) should close the dialog — the
			// `DocumentVersioningDialog` cleanup effect runs the purge
			// once at unmount via `markModified`/`didModifyRef`.

			return result;
		}
	);
	restoreAtom.debugLabel = `gitRestoreToParent/${documentId}`;
	return restoreAtom;
}, isEqual);

/**
 * Input for {@link $gitUndo}.
 *
 * `count` is the number of *user-meaningful* operations to undo
 * (resolves, commits, describes, restores, rebases). Snapshot ops and
 * prior `undo` ops are skipped without consuming budget — clicking the
 * dialog's "Undo" with N pending resolves should pass `count: N` so
 * every resolve in the session is reverted in a single backend call.
 */
export type GitUndoInput = {
	count: number;
};

/**
 * Git Undo - Undo the most recent N JJ operations.
 *
 * This restores the repo state to before the last `count` user-
 * meaningful operations. Snapshot/undo metadata ops are skipped
 * automatically.
 */
export const $gitUndo = atomFamily((documentId: string | undefined) => {
	const undoAtom = atom(
		null,
		async (get, set, input: GitUndoInput): Promise<{ newCommitHash: string }> => {
			if (!documentId) {
				throw new Error("Document ID is required");
			}

			const path = await get($documentLocalPath(documentId));
			if (!path) {
				throw new Error("Document path not found");
			}

			const git = get($git);
			const result = await git.undo({ path, count: input.count });

			// Refresh history and status after undo. Drop cached
			// `$gitShow` entries since the undo rewound the op log and
			// commit hashes on the way back may differ from what the
			// cache holds for the current UI revision.
			set($gitHistory(documentId));
			set($gitStatus(documentId));
			purgeGitShowCache(documentId);

			// Deliberately do NOT call `$purgeDocument` here. Purging
			// unloads the document metadata, which makes `DocumentView`
			// re-render `<DocumentLoading />` in place of `<DocumentShell>`,
			// unmounting `DocumentTopBar` → the entire versioning dialog
			// mid-session. `$gitResolveConflict` and `$gitRestoreToParent`
			// follow the same contract: mutate the repo, refresh cached
			// read atoms, and let `DocumentVersioningDialog`'s unmount
			// cleanup effect call `purgeDocument` once the dialog is
			// actually closing (signalled by `onTreeRewritten` →
			// `markModified`). Purging here would lose `undoableCount`,
			// blade selection, `hasAutoOpened` state, etc.

			return result;
		}
	);
	undoAtom.debugLabel = `gitUndo/${documentId}`;
	return undoAtom;
}, isEqual);
